import { FlagIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import Bold from '@tiptap/extension-bold';
import BubbleMenu from '@tiptap/extension-bubble-menu';
import { Color } from '@tiptap/extension-color';
import Dropcursor from '@tiptap/extension-dropcursor';
import Gapcursor from '@tiptap/extension-gapcursor';
import Heading from '@tiptap/extension-heading';
import Italic from '@tiptap/extension-italic';
import Link from '@tiptap/extension-link';
import Paragraph from '@tiptap/extension-paragraph';
import Strike from '@tiptap/extension-strike';
import Text from '@tiptap/extension-text';
import { TextAlign } from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useState } from 'react';
import {
    FormProvider,
    SubmitHandler,
    useForm,
    useWatch,
} from 'react-hook-form';

import { Button } from '@/components/core/Button';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogHeaderDefault,
    DialogTabsList,
    DialogTabsTrigger,
} from '@/components/core/Dialog';
import { Icon } from '@/components/core/Icon';
import { Input } from '@/components/core/Input';
import { Tabs, TabsContent } from '@/components/core/Tabs';
import ConfirmationModal from '@/dialogs/confirmation-modals/ConfirmationModal';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { isMicroFrontend } from '@/services/utils';

import {
    useCreateEnhancedContentEntry,
    useUpdateEnhancedContentEntry,
} from '../enhanced-content.queries';
import { EnhancedContentDataType } from '../enhanced-content.types';
import { DestinationsTab } from './DestinationsTab';
import { defaultBlocks } from './EditorTab/default-ui-blocks';
import { EditorTab } from './EditorTab/EditorTab';
import Document from './EditorTab/extensions/Document';
import DraggableNode from './EditorTab/extensions/DraggableNode';
import EdgeNode from './EditorTab/extensions/EdgeNode';
import { FontSize } from './EditorTab/extensions/FontSize';
import ImageNode from './EditorTab/extensions/ImageNode';
import SingleColumnNode from './EditorTab/extensions/SingleColumnNode';
import ThreeColumnsNode from './EditorTab/extensions/ThreeColumnsNode';
import TwoColumnsNode from './EditorTab/extensions/TwoColumnsNode';
import VideoNode from './EditorTab/extensions/VideoNode';

interface ContentDialogProps {
    open: boolean;
    entity?: EnhancedContentDataType;
    onClose: () => void;
    canPublish: boolean;
    canApprove: boolean;
    isReviewer: boolean;
}

type FormInputs = {
    name: string;
    retailerLinkIds: {
        name: string;
        articleNumbers: { articleNumber: string }[];
    }[];
};

const ContentDialog = ({
    open,
    entity,
    onClose,
    canPublish,
    canApprove,
    isReviewer,
}: ContentDialogProps) => {
    const [isEditMode] = useState(entity ? true : false);
    const [isConfirmatiomModalOpen, setIsConfirmatiomModalOpen] =
        useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [currentTab, setCurrentTab] = useState('content');

    const { t } = useTranslation();
    const { toast } = useToast();

    const defaultRetailerLinkIds = Object.keys(
        entity?.retailerLinkIds || {},
    ).map((key) => ({
        name: key,
        articleNumbers: entity?.retailerLinkIds[key].map((articleNumber) => ({
            articleNumber: articleNumber,
        })),
    }));
    const form = useForm<FormInputs>({
        defaultValues: {
            name: entity?.name || '',
            retailerLinkIds: defaultRetailerLinkIds,
        },
    });
    const initialContent =
        entity && entity.layoutBlocks && entity.layoutBlocks[0]
            ? entity.layoutBlocks[0].blockParams['body']
            : // Starting with the default block number 6, the three images and a description if we are in a new state.
            `<div data-type="edge-node"></div>${defaultBlocks[5].script}`;

    const formData = useWatch({
        control: form.control,
    });

    const editor = useEditor({
        extensions: [
            // TipTap extensions
            BubbleMenu,
            Dropcursor,
            Heading,
            Paragraph,
            Text,
            Bold,
            Italic,
            Strike,
            Color,
            StarterKit,
            TextStyle,
            Link,
            Gapcursor,
            TextAlign.configure({
                types: ['paragraph', 'heading'], // Apply text alignment to paragraphs and headings
            }),

            // Custom (in-house) extensions
            Document,
            DraggableNode,
            EdgeNode,
            ThreeColumnsNode,
            TwoColumnsNode,
            SingleColumnNode,
            ImageNode,
            VideoNode,
            FontSize
        ],

        // Content of the editor are actually governed by
        // the document schema (check extensions/Document) and this only
        // represents what the initial content of the editor is.
        content: initialContent,
        editorProps: {
            attributes: {
                class: 'focus:tw-outline-none'
            },
        },
        onUpdate: () => {
            const currentContent = editor?.getHTML();
            // Compare previous content with the current content
            if (initialContent && initialContent !== currentContent) {
                setHasChanges(true);
            } else {
                setHasChanges(false);
            }
        },
        // Check if the user is a "Reviewer" => should not be able to edit content
        editable: !isReviewer
    });

    const createEnhancedEntryMutation = useCreateEnhancedContentEntry({
        onError: (error) => {
            if (error.response.data) {
                toast.error(error.response.data.message);
            } else {
                toast.error('ENHANCED_CONTENT.ERROR_OCCURRED');
            }
        },
        onSuccess: () => {
            toast.success('ENHANCED_CONTENT.SAVED_SUCCESSFULLY');
            onClose();
        },
    });

    const updateEnhancedEntryMutation = useUpdateEnhancedContentEntry({
        onError: (error) => {
            if (error.response.data) {
                toast.error(error.response.data.message);
            } else {
                toast.error('ENHANCED_CONTENT.ERROR_OCCURRED');
            }
        },
        onSuccess: () => {
            toast.success('ENHANCED_CONTENT.SAVED_SUCCESSFULLY');
            onClose();
        },
    });

    const save = (data: FormInputs, isSaveAndPublish: boolean) => {
        const mappedRetailerLinkIds: Record<string, string[]> = {};
        if (data.retailerLinkIds) {
            data.retailerLinkIds.forEach(
                (link) =>
                    (mappedRetailerLinkIds[link.name] = link.articleNumbers.map(
                        (articleNumber) => articleNumber.articleNumber,
                    )),
            );
        }

        const entry: EnhancedContentDataType = {
            needsPublishing: isSaveAndPublish,
            name: data.name,
            retailerLinkIds: mappedRetailerLinkIds,
            layoutBlocks: [
                {
                    templateName: 'BODY',
                    blockParams: {
                        body: editor?.getHTML().replace(/<p>\s*<\/p>/g, '<br>') ||
                        '',
                    },
                },
            ],
        };

        if (!isEditMode) {
            createEnhancedEntryMutation.mutate({
                entry: entry,
            });
        } else {
            entry.id = entity?.id;
            updateEnhancedEntryMutation.mutate({
                entry: entry,
            });
        }
    };

    const onSave: SubmitHandler<FormInputs> = (data) => {
        save(data,false);
    };

    const onSaveAndPublish: SubmitHandler<FormInputs> = (data) => {
        save(data,true);
    };

    const onCloseModal = () => {
        setShowConfirmationModal(false);
        setIsConfirmatiomModalOpen(false);
        onClose();
    };

    const close = () => {
        if (hasChanges) {
            setShowConfirmationModal(true);
            setIsConfirmatiomModalOpen(true);
        } else {
            onClose();
        }
    };
    const isDisabled = !hasChanges && !form.formState.isDirty;
    return (
        <Dialog open={open} onOpenChange={close}>
            <DialogContent
                variant="large"
                useReactPortal={!isMicroFrontend}
            >
                {isConfirmatiomModalOpen && (
                    <ConfirmationModal
                        title={t('MODAL.CONFIRM_HEADER')}
                        subTitle= {t('MODAL.DISMISS_WITH_UNSAVED_CHANGES')}
                        onSubmit={onCloseModal}
                        onClose={() => setShowConfirmationModal(false)}
                        show={showConfirmationModal}
                    />
                )}

                <DialogHeader>
                    <DialogHeaderDefault>
                        {t('ENHANCED_CONTENT.NEW_CONTENT')}
                    </DialogHeaderDefault>
                </DialogHeader>
                <DialogBody padding="none" className="tw-flex tw-flex-col tw-min-h-0 tw-h-[80vh]">
                    <Tabs
                        defaultValue="content"
                        className="tw-flex tw-flex-col tw-min-h-0 tw-flex-1"
                        onValueChange={tab => setCurrentTab(tab)}
                    >
                        <FormProvider {...form}>
                            <div className="tw-flex tw-flex-col sm:tw-flex-row tw-justify-center tw-items-center tw-w-full sm:tw-w-1/2 tw-px-6 tw-py-3">
                                <label className="tw-text-t-sm tw-m-0 tw-mb-2 sm:tw-mr-1 sm:tw-mb-0">
                                    {t('ENHANCED_CONTENT.ENTRY_NAME')}
                                </label>
                                <Input
                                    className="tw-flex tw-h-9 tw-w-full tw-bg-transparent tw-px-3 tw-py-2 tw-text-t-sm tw-ring-offset-background
                                    disabled:tw-cursor-not-allowed disabled:tw-opacity-50 tw-flex-1"
                                    placeholder={t('ENHANCED_CONTENT.ENTRY_NAME')}
                                    {...form.register('name', { required: true })}
                                    disabled ={isReviewer}
                                />
                            </div>

                            <DialogTabsList className="tw-left-[-56px] tw-z-50">
                                <DialogTabsTrigger value="content" className='tw-flex tw-items-center tw-justify-center'>
                                    <Icon
                                        icon={PencilSquareIcon}
                                        size='md'
                                        variant='dark'
                                        className='tw-object-contain !tw-mt-[-3px]'
                                    />
                                </DialogTabsTrigger>
                                <DialogTabsTrigger value="destinations" className='tw-flex tw-items-center tw-justify-center'>
                                    <Icon
                                        icon={FlagIcon}
                                        size='md'
                                        variant='dark'
                                        className='tw-object-contain !tw-mt-[-3px]'
                                    />
                                </DialogTabsTrigger>
                            </DialogTabsList>

                            <TabsContent
                                value="content"
                                className="tw-flex-1 tw-min-h-0 tw-mt-0"
                            >
                                <EditorTab editor={editor} showBlocks= {!isReviewer} />
                            </TabsContent>

                            <TabsContent
                                value="destinations"
                                className="tw-flex-1 tw-min-h-0 tw-mt-0"
                            >
                                <DestinationsTab isReviewer={isReviewer}/>
                            </TabsContent>
                        </FormProvider>
                    </Tabs>
                </DialogBody>
                <DialogFooter>
                    {!isReviewer &&
                    <>
                        <Button
                            type="submit"
                            className="tw-mr-1"
                            disabled={
                                isDisabled
                            }
                            onClick={() => form.handleSubmit(onSave)()}
                        >
                            {t('ENHANCED_CONTENT.SAVE_ENTRY')}
                        </Button>
                        <Button
                            type="submit"
                            disabled={
                                !form.formState.isValid ||
                                formData.retailerLinkIds === undefined ||
                                formData.retailerLinkIds.length === 0 ||
                                isDisabled
                            }
                            onClick={() =>
                                form.handleSubmit(onSaveAndPublish)()
                            }
                        >
                            {!canApprove && canPublish
                                ? t('ENHANCED_CONTENT.PUBLISH')
                                : t('ENHANCED_CONTENT.SAVE_AND_PUBLISH_ENTRY')}
                        </Button>

                        <Button className="tw-mr-1" onClick={() => editor?.chain().focus().redo().run()} disabled={!editor?.can().redo() || currentTab !== 'content'} variant="secondary">
                            {t('REDO')}
                        </Button>
                        <Button className="tw-mr-1" onClick={() => editor?.chain().focus().undo().run()} disabled={!editor?.can().undo() || currentTab !== 'content'} variant="secondary">
                            {t('UNDO')}
                        </Button>
                    </>}
                    <Button
                        className="tw-mr-1"
                        onClick={close}
                        variant="secondary"
                    >
                        {t('CLOSE')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export { ContentDialog };
